<template>
  <!--
    This example requires updating your template:

    ```
    <html class="h-full bg-white dark:bg-gray-900">
    <body class="h-full">
    ```
  -->
  <div>
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog class="relative z-50 lg:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to=""
          leave="transition-opacity ease-linear duration-300"
          leave-from=""
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80"></div>
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to=""
                leave="ease-in-out duration-300"
                leave-from=""
                leave-to="opacity-0"
              >
                <div class="absolute top-0 left-full flex w-16 justify-center pt-5">
                  <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon class="size-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>

              <!-- Sidebar component, swap this element with another sidebar if you like -->
              <div
                class="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 dark:bg-gray-900 dark:ring dark:ring-white/10 dark:before:pointer-events-none dark:before:absolute dark:before:inset-0 dark:before:bg-black/10"
              >
                <div class="relative flex h-16 shrink-0 items-center">
                  <img class="h-8 w-auto dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
                  <img class="h-8 w-auto not-dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
                </div>
                <nav class="relative flex flex-1 flex-col">
                  <ul role="list" class="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" class="-mx-2 space-y-1">
                        <li v-for="item in navigation" :key="item.name">
                          <router-link
                            :to="item.to"
                            :class="[
                              isActive(item.to)
                                ? 'bg-gray-50 text-indigo-600 dark:bg-white/5 dark:text-white'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white',
                              'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                            ]"
                          >
                            <component
                              :is="item.icon"
                              :class="[
                                isActive(item.to)
                                  ? 'text-indigo-600 dark:text-white'
                                  : 'text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-white',
                                'size-6 shrink-0',
                              ]"
                              aria-hidden="true"
                            />
                            {{ item.name }}
                          </router-link>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden bg-gray-900 lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-56 xl:w-72 lg:flex-col">
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div
        class="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4 dark:border-white/10 dark:bg-black/10"
      >
        <div class="flex h-16 shrink-0 items-center">
          <img class="h-8 w-auto dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
          <img class="h-8 w-auto not-dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        </div>
        <nav class="flex flex-1 flex-col">
          <ul role="list" class="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" class="-mx-2 space-y-1">
                <li v-for="item in navigation" :key="item.name">
                  <router-link
                    :to="item.to"
                    :class="[
                      isActive(item.to)
                        ? 'bg-gray-50 text-indigo-600 dark:bg-white/5 dark:text-white'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white',
                      'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        isActive(item.to)
                          ? 'text-indigo-600 dark:text-white'
                          : 'text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-white',
                        'size-6 shrink-0',
                      ]"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div class="lg:pl-56 xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8 dark:border-white/10 dark:bg-gray-900 dark:shadow-none"
      >
        <button
          type="button"
          class="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900 lg:hidden dark:text-gray-400 dark:hover:text-white"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <Bars3Icon class="size-6" aria-hidden="true" />
        </button>

        <!-- Separator -->
        <div class="h-6 w-px bg-gray-200 lg:hidden dark:bg-white/10" aria-hidden="true"></div>

        <div class="flex min-w-0 flex-1 gap-x-4 self-stretch lg:gap-x-6">
          <OrganizationSwitcher />

          <button
            v-if="
              organizations.canManageBilling &&
              subscription?.flavor === 'BASIC' &&
              ['ACTIVE', 'ON_HOLD'].includes(subscription?.status)
            "
            @click="$router.push('/billing')"
            class="self-center hidden sm:inline-flex items-center gap-x-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <SparklesIcon class="-ml-0.5 size-4" aria-hidden="true" />
            Upgrade to Pro
          </button>

          <form v-if="SHOW_SEARCH" class="grid flex-1 grid-cols-1" action="#" method="GET">
            <input
              name="search"
              aria-label="Search"
              class="col-start-1 row-start-1 block size-full bg-white pl-8 text-base text-gray-900 outline-hidden border-none ring-0 focus:outline-none focus:ring-0 placeholder:text-gray-400 sm:text-sm/6 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500"
              placeholder="Search"
            />
            <MagnifyingGlassIcon
              class="pointer-events-none col-start-1 row-start-1 size-5 self-center text-gray-400"
              aria-hidden="true"
            />
          </form>
          <div class="ml-auto flex items-center gap-x-4 lg:gap-x-6">
            <button
              type="button"
              class="-m-2.5 p-2.5 opacity-1 text-gray-400 hover:text-gray-500 dark:hover:text-white"
            >
              <span class="sr-only">View notifications</span>
              <BellIcon class="size-6" aria-hidden="true" />
            </button>

            <!-- Separator -->
            <div
              class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200 dark:lg:bg-white/10"
              aria-hidden="true"
            ></div>

            <!-- Profile dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="relative flex items-center">
                <span class="absolute -inset-1.5"></span>
                <span class="sr-only">Open user menu</span>
                <img
                  class="size-8 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"
                  :src="gravatarUrl(auth.username, { size: 128, defaultImage: 'retro' })"
                  :alt="`${auth.displayName} avatar`"
                />
                <span class="hidden lg:flex lg:items-center">
                  <span
                    class="ml-4 text-sm/6 font-semibold text-gray-900 dark:text-white"
                    aria-hidden="true"
                    >{{ auth.displayName }}</span
                  >
                  <ChevronDownIcon
                    class="ml-2 size-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </MenuButton>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg outline-1 outline-gray-900/5 dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
                >
                  <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                    <button
                      v-if="item.onClick"
                      type="button"
                      @click.prevent="item.onClick"
                      :class="[
                        active ? 'bg-gray-50 outline-hidden dark:bg-white/5' : '',
                        'block w-full text-left px-3 py-1 text-sm/6 text-gray-900 dark:text-white',
                      ]"
                    >
                      {{ item.name }}
                    </button>
                    <a
                      v-else
                      :href="item.href"
                      :class="[
                        active ? 'bg-gray-50 outline-hidden dark:bg-white/5' : '',
                        'block px-3 py-1 text-sm/6 text-gray-900 dark:text-white',
                      ]"
                      >{{ item.name }}</a
                    >
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>

      <main class="py-10">
        <div class="px-4 sm:px-6 lg:px-8">
          <!-- Keyed by organization so pages reload their organization-scoped data on a switch. -->
          <RouterView :key="organizations.selectedId ?? ''" />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionStore } from '@/stores/subscription';
import { useOrganizationStore } from '@/stores/organization';
import OrganizationSwitcher from '@/components/OrganizationSwitcher.vue';
import { useRouter, useRoute } from 'vue-router';
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import {
  ChartBarIcon,
  Bars3Icon,
  BellIcon,
  BuildingOffice2Icon,
  CircleStackIcon,
  Cog6ToothIcon,
  HomeIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon, MagnifyingGlassIcon, SparklesIcon } from '@heroicons/vue/20/solid';
import gravatarUrl from '@/lib/gravatar';

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;
const auth = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const organizations = useOrganizationStore();
const subscription = computed(() => subscriptionStore.subscription);
const router = useRouter();
const $route = useRoute();

function onSignOut() {
  try {
    auth.logout();
  } finally {
    router.push({ name: 'Login' });
  }
}

const navigation = [
  { name: 'Dashboard', to: '/', icon: HomeIcon },
  { name: 'Caches', to: '/caches', icon: CircleStackIcon },
  { name: 'Metrics', to: '/metrics', icon: ChartBarIcon },
  { name: 'Organization', to: '/organization', icon: BuildingOffice2Icon },
  { name: 'Settings', to: '/profile', icon: Cog6ToothIcon },
];
function isActive(to: string) {
  return to === '/' ? $route.path === '/' : $route.path === to || $route.path.startsWith(`${to}/`);
}
const userNavigation = [
  { name: 'Your profile', href: '/profile' },
  { name: 'Sign out', onClick: onSignOut },
];

// Fixed (non-user-configurable) flag to control search visibility
const SHOW_SEARCH = false;

const sidebarOpen = ref(false);
</script>
